import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, TrendingDown, Percent } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardHeader, CardBody, Table, Skeleton } from '../../components/common';
import { reportApi } from '../../services/api';
import './Reports.css';

export default function SavingsReport() {
    const { data, isLoading } = useQuery({
        queryKey: ['report-savings'],
        queryFn: () => reportApi.getSavings(),
        select: (res) => res.data?.data || res.data || {}
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const formatShortCurrency = (value) => {
        if (value >= 1000000000) {
            return `Rp ${(value / 1000000000).toFixed(1)}B`;
        }
        if (value >= 1000000) {
            return `Rp ${(value / 1000000).toFixed(1)}M`;
        }
        return formatCurrency(value);
    };

    const savingsData = data || {};
    const summary = savingsData.summary || {
        totalBudget: 0,
        totalSpent: 0,
        totalSavings: 0,
        savingsPercentage: 0
    };
    const byCategory = savingsData.byCategory || [];
    const byDepartment = savingsData.byDepartment || [];

    const categoryColumns = [
        {
            key: 'category',
            title: 'Category',
            dataIndex: 'category'
        },
        {
            key: 'budgeted',
            title: 'Budgeted',
            dataIndex: 'budgeted',
            align: 'right',
            render: (val) => formatCurrency(val)
        },
        {
            key: 'spent',
            title: 'Actual Spent',
            dataIndex: 'spent',
            align: 'right',
            render: (val) => formatCurrency(val)
        },
        {
            key: 'savings',
            title: 'Savings',
            dataIndex: 'savings',
            align: 'right',
            render: (val) => (
                <span className={val >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(val)}
                </span>
            )
        },
        {
            key: 'percentage',
            title: '% Savings',
            dataIndex: 'percentage',
            align: 'right',
            render: (val) => (
                <span className={val >= 0 ? 'positive' : 'negative'}>
                    {val?.toFixed(1) || 0}%
                </span>
            )
        }
    ];

    return (
        <div className="report-page">
            <PageHeader
                title="Savings Analysis"
                subtitle="Analyze cost savings from procurement activities"
                breadcrumbs={[
                    { label: 'Reports', path: '/reports' },
                    { label: 'Savings Analysis' }
                ]}
            />

            {/* Summary Cards */}
            <div className="report-summary-cards">
                <Card className="summary-card primary">
                    <CardBody>
                        <DollarSign size={24} />
                        <div>
                            <span className="summary-value">{formatShortCurrency(summary.totalBudget)}</span>
                            <span className="summary-label">Total Budget</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card info">
                    <CardBody>
                        <TrendingDown size={24} />
                        <div>
                            <span className="summary-value">{formatShortCurrency(summary.totalSpent)}</span>
                            <span className="summary-label">Total Spent</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card success">
                    <CardBody>
                        <TrendingUp size={24} />
                        <div>
                            <span className="summary-value">{formatShortCurrency(summary.totalSavings)}</span>
                            <span className="summary-label">Total Savings</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card warning">
                    <CardBody>
                        <Percent size={24} />
                        <div>
                            <span className="summary-value">{summary.savingsPercentage?.toFixed(1) || 0}%</span>
                            <span className="summary-label">Savings Rate</span>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* By Category */}
            <Card>
                <CardHeader>
                    <h3>Savings by Category</h3>
                </CardHeader>
                <Table
                    columns={categoryColumns}
                    data={byCategory}
                    loading={isLoading}
                    emptyText="No data available"
                    rowKey="category"
                />
            </Card>

            {/* By Department */}
            <Card style={{ marginTop: '1.5rem' }}>
                <CardHeader>
                    <h3>Savings by Department</h3>
                </CardHeader>
                <Table
                    columns={[
                        { key: 'department', title: 'Department', dataIndex: 'department' },
                        { key: 'budgeted', title: 'Budgeted', dataIndex: 'budgeted', align: 'right', render: (val) => formatCurrency(val) },
                        { key: 'spent', title: 'Actual Spent', dataIndex: 'spent', align: 'right', render: (val) => formatCurrency(val) },
                        { key: 'savings', title: 'Savings', dataIndex: 'savings', align: 'right', render: (val) => <span className={val >= 0 ? 'positive' : 'negative'}>{formatCurrency(val)}</span> }
                    ]}
                    data={byDepartment}
                    loading={isLoading}
                    emptyText="No data available"
                    rowKey="department"
                />
            </Card>
        </div>
    );
}
